import { useEffect, useState, useCallback } from "react";
import api from "../../../utils/axios";
import { Spin } from "antd";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import Pagination from "../../components/Pagination";
import { getAlert, getDataWithComp } from "../../../utils/SamuiUtils";
import DataTable from "../../../components/DataTable";
import { normalizeImageValue, buildImageSrc } from "../../components/Modal/ItemModalImageUtils";

const STATUS_OPTIONS = [
  { value: "", label: "ทั้งหมด" },
  { value: "1", label: "รออนุมัติ" },
  { value: "4", label: "อนุมัติแล้ว รอช่างรับ" },
  { value: "2", label: "ช่างเซ็นรับแล้ว" },
  { value: "3", label: "ถูกยกเลิก" },
];

const STATUS_BADGE = {
  1: { label: "รออนุมัติ", color: "#f59e0b", bg: "#fef3c7" },
  4: { label: "อนุมัติแล้ว รอช่างรับ", color: "#2563eb", bg: "#dbeafe" },
  2: { label: "ช่างเซ็นรับแล้ว", color: "#16a34a", bg: "#dcfce7" },
  3: { label: "ถูกยกเลิก", color: "#dc2626", bg: "#fee2e2" },
};

const THEME = "#E13F57";
/** หัวตารางรายละเอียด / รายการสินค้า (ให้ตรงกับตารางขวา) */
const TABLE_HEADER_BG = "#E13F57";
const MIN_LOADING_MS = 1000;

const ensureMinLoadingTime = async (startedAt, minMs = MIN_LOADING_MS) => {
  const elapsed = Date.now() - startedAt;
  const remain = minMs - elapsed;
  if (remain > 0) {
    await new Promise((resolve) => setTimeout(resolve, remain));
  }
};

const StatusBadge = ({ status }) => {
  const s = STATUS_BADGE[status] || { label: String(status), color: "#6b7280", bg: "#f3f4f6" };
  return (
    <span style={{
      display: "inline-block",
      padding: "3px 10px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: "600",
      color: s.color,
      backgroundColor: s.bg,
      whiteSpace: "nowrap",
    }}>
      {s.label}
    </span>
  );
};

const formatDate = (val) => {
  if (!val) return "-";
  const d = new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString("th-TH", {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

/** ค่า placeholder "-" ให้อยู่กึ่งกลางเซลล์ / บล็อก */
function dashCentered(content) {
  if (content === null || content === undefined) return content;
  if (typeof content === "object") return content;
  const s = String(content).trim();
  if (s !== "-") return content;
  return (
    <span style={{ display: "block", width: "100%", textAlign: "center" }}>-</span>
  );
}

/** แยกว่า Doc_Type เป็นแนว IN / OUT ดิบ (ก่อนหมุนมุมมองคลังรถ) */
function classifyRawDocLineMovement(item) {
  const raw = item?.Doc_Type ?? item?.doc_type;
  const str = String(raw == null ? "" : raw).trim();
  const n = Number(str);
  const isOutLike =
    str === "O" || str === "AO" || str === "o" || str === "ao" ||
    (Number.isFinite(n) && n === 2);
  const isInLike =
    str === "I" || str === "AI" || str === "i" || str === "ai" ||
    (Number.isFinite(n) && n === 1);
  return { isOutLike, isInLike, str };
}

/** คลังรถ = Tb_Set_WH ของทีม (team_wh_id) ถ้ามี ไม่เช่นนั้นใช้ t_wh เป็นคลังปลายทางเดิม */
function resolveVehicleWarehouseContext(docHeader) {
  const teamWhId = Number(docHeader?.team_wh_id ?? docHeader?.Team_WH_Id ?? 0) || null;
  const tWhId = Number(docHeader?.t_wh_id ?? docHeader?.T_WH_Id ?? 0) || null;
  const fWhId = Number(docHeader?.f_wh_id ?? docHeader?.F_WH_Id ?? 0) || null;
  const teamWhName = docHeader?.team_wh_name || docHeader?.Team_WH_Name || "";
  const tName = docHeader?.t_wh_name || docHeader?.T_WH_Name || "";
  const fName = docHeader?.f_wh_name || docHeader?.F_WH_Name || "";
  const vehicleId = teamWhId || tWhId || null;
  const vehicleName = (teamWhId && teamWhName) ? teamWhName : (tName || fName);
  return { teamWhId, tWhId, fWhId, vehicleId, vehicleName, tName, fName };
}

/**
 * IN/OUT มองจากคลังรถ — รายการใบขอเบิกมักเป็น Doc_Type = 3 (ตัวเลข) ไม่ใช่ I/O ตัวอักษร
 */
function getVehicleWarehouseInOutLabel(item, docHeader) {
  const { vehicleId, tWhId, fWhId } = resolveVehicleWarehouseContext(docHeader);
  const rowWhId = Number(
    item?.WH_Id ?? item?.Wh_Id ?? item?.wh_id ?? item?.T_WH_Id ?? item?.F_WH_Id ?? 0,
  ) || null;
  const raw = item?.Doc_Type ?? item?.doc_type;
  const n = Number(raw);
  const { isOutLike, isInLike } = classifyRawDocLineMovement(item);
  const lineIsWithdrawNumeric = Number.isFinite(n) && n === 3;

  if (vehicleId && fWhId && tWhId && fWhId !== tWhId) {
    if (lineIsWithdrawNumeric || (!isOutLike && !isInLike)) {
      if (tWhId === vehicleId) return "IN";
      if (fWhId === vehicleId) return "OUT";
    }
    if (rowWhId === tWhId) {
      if (isInLike) return "IN";
      if (isOutLike) return "OUT";
    }
    if (rowWhId === fWhId && isOutLike) return "IN";
    if (rowWhId === fWhId && isInLike) return "OUT";
    if (!rowWhId && isOutLike) return fWhId === vehicleId ? "OUT" : "IN";
    if (!rowWhId && isInLike) return "IN";
  }

  if (isInLike) return "IN";
  if (isOutLike) return "OUT";
  return raw != null && String(raw) !== "" ? String(raw) : null;
}

function historyWarehouseSummaryCell(r) {
  if (r._jobCloseOut) {
    const name = r.vehicle_wh_name || r.team_wh_name || (r.stc_wh_id ? `คลัง #${r.stc_wh_id}` : "คลังรถ");
    return (
      <span>
        <span style={{ color: "#dc2626", fontWeight: 700, fontSize: 12, marginRight: 6 }}>OUT</span>
        <span style={{ color: "#111827" }}>{name}</span>
      </span>
    );
  }

  const { vehicleId, tWhId, fWhId, vehicleName, tName, fName } = resolveVehicleWarehouseContext(r);

  if (vehicleId && fWhId && tWhId && fWhId !== tWhId) {
    if (tWhId === vehicleId) {
      return (
        <span>
          <span style={{ color: "#16a34a", fontWeight: 700, fontSize: 12, marginRight: 6 }}>IN</span>
          <span style={{ color: "#111827" }}>{vehicleName || tName}</span>
        </span>
      );
    }
    if (fWhId === vehicleId) {
      return (
        <span>
          <span style={{ color: "#dc2626", fontWeight: 700, fontSize: 12, marginRight: 6 }}>OUT</span>
          <span style={{ color: "#111827" }}>{vehicleName || fName}</span>
        </span>
      );
    }
  }

  if (tName && fName && tWhId && fWhId && tWhId !== fWhId) {
    return (
      <span>
        <span style={{ color: "#16a34a", fontWeight: 700, fontSize: 12, marginRight: 6 }}>IN</span>
        <span style={{ color: "#111827" }}>{tName}</span>
      </span>
    );
  }
  if (tName) {
    return (
      <span>
        <span style={{ color: "#16a34a", fontWeight: 700, fontSize: 12, marginRight: 6 }}>IN</span>
        <span style={{ color: "#111827" }}>{tName}</span>
      </span>
    );
  }
  if (fName) {
    return (
      <span>
        <span style={{ color: "#dc2626", fontWeight: 700, fontSize: 12, marginRight: 6 }}>OUT</span>
        <span style={{ color: "#111827" }}>{fName}</span>
      </span>
    );
  }
  return "-";
}

/** Doc_NoRef = JOB:{Contract_Id} ตาม jobSubmit (job_id = Contract_Id) */
function parseContractIdFromStcRow(stc) {
  const ref = String(stc.Doc_NoRef ?? stc.doc_no_ref ?? "").trim();
  const mJob = ref.match(/^JOB:(\d+)$/i);
  if (mJob) {
    const n = parseInt(mJob[1], 10);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const remark = String(stc.STC_Remark ?? stc.stc_remark ?? "");
  const mRm = remark.match(/Job_Id\s*=\s*(\d+)/i);
  if (mRm) {
    const n = parseInt(mRm[1], 10);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

/** Doc_Id (CT_H) -> Doc_No เลขที่สัญญา */
async function fetchContractDocNoMap(contractIds) {
  const out = {};
  const ids = [...new Set(contractIds)].filter((id) => Number.isFinite(id) && id > 0);
  const CHUNK = 120;
  for (let i = 0; i < ids.length; i += CHUNK) {
    const slice = ids.slice(i, i + CHUNK);
    try {
      const rows = await getDataWithComp(
        "CT_H",
        `AND Doc_Id IN (${slice.join(",")})`,
        { fields: ["Doc_Id", "Doc_No"] },
      );
      (Array.isArray(rows) ? rows : []).forEach((h) => {
        const id = Number(h.Doc_Id ?? h.doc_id);
        if (!Number.isFinite(id)) return;
        const no = String(h.Doc_No ?? h.doc_no ?? "").trim();
        if (no) out[id] = no;
      });
    } catch {
      /* ignore chunk */
    }
  }
  return out;
}

/** Calendar_Id -> Contract_Id (แถว STC เก่าที่ไม่มี Doc_NoRef) */
async function fetchContractIdByCalendarIdMap(calendarIds) {
  const out = {};
  const ids = [...new Set(calendarIds)].filter((id) => Number.isFinite(id) && id > 0);
  const CHUNK = 120;
  for (let i = 0; i < ids.length; i += CHUNK) {
    const slice = ids.slice(i, i + CHUNK);
    try {
      const rows = await getDataWithComp(
        "CT_JOB_CALENDAR",
        `AND Calendar_Id IN (${slice.join(",")}) AND IsActive = 1`,
        { fields: ["Calendar_Id", "Contract_Id"] },
      );
      (Array.isArray(rows) ? rows : []).forEach((r) => {
        const cal = Number(r.Calendar_Id ?? r.calendar_id);
        const cid = Number(r.Contract_Id ?? r.contract_id);
        if (Number.isFinite(cal) && Number.isFinite(cid) && cid > 0) out[cal] = cid;
      });
    } catch {
      /* ignore chunk */
    }
  }
  return out;
}

/** Round_No จากปฏิทิน (ตรงกับ logic join ใน get-assigned-jobs) */
async function fetchRoundNoByCalendarIds(calendarIds) {
  const out = {};
  const ids = [...new Set(calendarIds)].filter((id) => Number.isFinite(id) && id > 0);
  const compId = typeof window !== "undefined" ? window.localStorage.getItem("company") : null;
  const CHUNK = 10;
  for (let i = 0; i < ids.length; i += CHUNK) {
    const slice = ids.slice(i, i + CHUNK);
    await Promise.all(
      slice.map(async (calId) => {
        try {
          const res = await api.get("/get-assigned-jobs", {
            params: {
              ...(compId ? { comp_id: compId } : {}),
              calendar_id: calId,
            },
          });
          const ev = res.data?.result?.[0];
          if (!ev) return;
          const rn = ev.Round_No ?? ev.round_no;
          if (rn != null && String(rn).trim() !== "") {
            const n = Number(rn);
            out[calId] = Number.isFinite(n) ? n : rn;
          }
        } catch {
          /* skip */
        }
      }),
    );
  }
  return out;
}

/** แสดงในคอลัมน์เดิมที่เคยเป็นทีม — งานรอบที่เท่าไหร่ */
function formatJobRoundCell(row) {
  if (!row) return "-";
  const rn = row.round_no ?? row.Round_No;
  const rid = row.round_id ?? row.Round_Id;
  if (rn != null && String(rn).trim() !== "") {
    const n = Number(rn);
    if (Number.isFinite(n)) return `รอบที่ ${n}`;
    return `รอบที่ ${rn}`;
  }
  if (rid != null && String(rid).trim() !== "") return `รอบ (Round_Id ${rid})`;
  return "-";
}

/** รวมประวัติตัดสต็อกคลังรถจากปิดงาน (WH_ITEM_STC Doc_type=AO) — ไม่มีในใบเบิก WD */
function buildJobCloseHistoryFromStc(
  stcRows,
  whNameById,
  contractDocNoMap = {},
  calendarToContractId = {},
  calendarRoundNoMap = {},
) {
  const map = whNameById && typeof whNameById === "object" ? whNameById : {};
  const cMap = contractDocNoMap && typeof contractDocNoMap === "object" ? contractDocNoMap : {};
  const calMap = calendarToContractId && typeof calendarToContractId === "object" ? calendarToContractId : {};
  const rRoundMap = calendarRoundNoMap && typeof calendarRoundNoMap === "object" ? calendarRoundNoMap : {};
  const groups = new Map();
  for (const stc of stcRows) {
    const docId = stc.Doc_Id ?? stc.doc_id;
    const whId = stc.WH_Id ?? stc.wh_id;
    const docNo = String(stc.Doc_No ?? stc.doc_no ?? "").trim();
    const d = stc.STC_Date ?? stc.stc_date;
    const nDoc = Number(docId);
    const nWh = Number(whId);
    if (!Number.isFinite(nDoc) || !Number.isFinite(nWh)) continue;
    const dayKey = d ? new Date(d).toISOString().slice(0, 10) : "";
    const key = `${nDoc}|${nWh}|${docNo}|${dayKey}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(stc);
  }
  const out = [];
  let seq = 0;
  for (const [, list] of groups) {
    const first = list[0];
    let contractId = null;
    for (const row of list) {
      contractId = parseContractIdFromStcRow(row);
      if (contractId != null) break;
    }
    const nDoc = Number(first.Doc_Id ?? first.doc_id);
    if (contractId == null && Number.isFinite(nDoc) && calMap[nDoc] != null) {
      contractId = calMap[nDoc];
    }
    const nWh = Number(first.WH_Id ?? first.wh_id);
    const docNo = String(first.Doc_No ?? first.doc_no ?? "").trim();
    const d = first.STC_Date ?? first.stc_date;
    const whName = map[nWh] || "";
    const contractNo = contractId != null ? String(cMap[contractId] || "").trim() : "";
    const roundNoRaw = Number.isFinite(nDoc) ? rRoundMap[nDoc] : undefined;
    let round_no = null;
    if (roundNoRaw != null && String(roundNoRaw).trim() !== "") {
      const n = Number(roundNoRaw);
      round_no = Number.isFinite(n) ? n : roundNoRaw;
    }
    seq += 1;
    out.push({
      _source: "job_close_stc",
      _jobCloseOut: true,
      wh_doc_id: -1000000000 - seq,
      stc_doc_id: nDoc,
      stc_wh_id: nWh,
      stc_doc_no: docNo,
      contract_id: contractId,
      stc_ao_doc_no: docNo,
      wh_doc_no: contractNo || docNo || `AO·ปิดงาน #${nDoc}`,
      round_no,
      team_name: null,
      team_wh_id: nWh,
      team_wh_name: whName,
      vehicle_wh_name: whName,
      f_wh_id: nWh,
      f_wh_name: whName,
      t_wh_id: null,
      t_wh_name: null,
      total_items: list.length,
      request_date: d,
      created_date: d,
      status: 2,
    });
  }
  return out;
}

export default function WithdrawApprovalPage() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("1");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSizeState, setPageSizeState] = useState(100);
  const PAGE_SIZE = pageSizeState;
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [leftTab, setLeftTab] = useState("list");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [historySelected, setHistorySelected] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyItemsLoading, setHistoryItemsLoading] = useState(false);

  // ---- Create modal ----
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [whList, setWhList] = useState([]);
  const [itemList, setItemList] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [createForm, setCreateForm] = useState({ f_wh_id: "", t_wh_id: "", remark: "" });
  const [createItems, setCreateItems] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);
  // ---- Item picker modal ----
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerGroup, setPickerGroup] = useState("");
  const [pickerChecked, setPickerChecked] = useState({}); // Item_Id -> qty
  const [onhandMap, setOnhandMap] = useState({}); // Item_Id -> total onhand

  const fetchRequests = useCallback(async () => {
    const startedAt = Date.now();
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (filterStatus) params.append("status", filterStatus);
      const res = await api.get(`/app/admin/withdraw-requests?${params.toString()}`);
      const all = res.data?.status === "OK" ? (res.data.results || []) : [];
      setRequests(all.filter((r) => !String(r.wh_doc_no || "").startsWith("CNT")));
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      setRequests([]);
      setCurrentPage(1);
    } finally {
      await ensureMinLoadingTime(startedAt);
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const fetchDetail = async (id) => {
    const startedAt = Date.now();
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await api.get(`/app/admin/withdraw-requests/${id}`);
      if (res.data?.status === "OK") setDetail(res.data.result);
    } catch (err) {
      console.error(err);
    } finally {
      await ensureMinLoadingTime(startedAt);
      setDetailLoading(false);
    }
  };

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const [res, aoRaw] = await Promise.all([
        api.get(`/app/admin/withdraw-requests?status=2&limit=500`),
        (async () => {
          try {
            return await getDataWithComp(
              "WH_ITEM_STC",
              "AND (Doc_type = 'AO' OR Doc_type = 'ao') AND (STC_Remark LIKE '%ปิดงาน%' OR STC_Remark LIKE '%ส่งงาน%') ORDER BY STC_Date DESC",
              { limit: 800 },
            );
          } catch (e) {
            console.warn("[WithdrawApproval] WH_ITEM_STC history:", e);
            return [];
          }
        })(),
      ]);
      const wd = (res.data?.status === "OK" ? (res.data.results || []) : []).filter(
        (r) => !String(r.wh_doc_no || "").startsWith("CNT"),
      );
      let whNameById = {};
      try {
        const whList = await getDataWithComp("Tb_Set_WH", "AND WH_Status = 'Y'");
        (Array.isArray(whList) ? whList : []).forEach((w) => {
          const id = Number(w.WH_Id);
          if (Number.isFinite(id)) whNameById[id] = w.WH_Name || "";
        });
      } catch {
        whNameById = {};
      }
      const aoList = Array.isArray(aoRaw) ? aoRaw : [];
      let contractIds = aoList.map(parseContractIdFromStcRow).filter((id) => id != null);
      const calIdsForLookup = [
        ...new Set(
          aoList
            .filter((row) => parseContractIdFromStcRow(row) == null)
            .map((row) => Number(row.Doc_Id ?? row.doc_id))
            .filter((id) => Number.isFinite(id) && id > 0),
        ),
      ];
      const calendarToContractId =
        calIdsForLookup.length > 0 ? await fetchContractIdByCalendarIdMap(calIdsForLookup) : {};
      contractIds = [...contractIds, ...Object.values(calendarToContractId)];
      const contractDocNoMap = await fetchContractDocNoMap(contractIds);
      const uniqueCalForRound = [
        ...new Set(
          aoList
            .map((row) => Number(row.Doc_Id ?? row.doc_id))
            .filter((id) => Number.isFinite(id) && id > 0),
        ),
      ];
      const calendarRoundNoMap =
        uniqueCalForRound.length > 0 ? await fetchRoundNoByCalendarIds(uniqueCalForRound) : {};
      const jobRows = buildJobCloseHistoryFromStc(
        aoList,
        whNameById,
        contractDocNoMap,
        calendarToContractId,
        calendarRoundNoMap,
      );
      const merged = [...jobRows, ...wd].sort((a, b) => {
        const ta = new Date(a.request_date || a.created_date || 0).getTime();
        const tb = new Date(b.request_date || b.created_date || 0).getTime();
        return tb - ta;
      });
      setHistory(merged);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (leftTab === "history") fetchHistory();
  }, [leftTab, fetchHistory]);

  const fetchHistoryItems = async (row) => {
    setHistorySelected(row);
    setHistoryItems([]);
    setHistoryItemsLoading(true);
    try {
      if (row._source === "job_close_stc" && row.stc_doc_id != null && row.stc_wh_id != null) {
        const rows = await getDataWithComp(
          "WH_ITEM_STC",
          `AND Doc_Id = ${Number(row.stc_doc_id)} AND WH_Id = ${Number(row.stc_wh_id)} AND (Doc_type = 'AO' OR Doc_type = 'ao') ORDER BY STC_Date DESC, STC_SEQ DESC`,
        );
        setHistoryItems(Array.isArray(rows) ? rows : []);
      } else {
        const rows = await getDataWithComp("API_1002_WHDOC_D", `AND WHDoc_Id = ${row.wh_doc_id} ORDER BY Line ASC`);
        setHistoryItems(Array.isArray(rows) ? rows : []);
      }
    } catch {
      setHistoryItems([]);
    } finally {
      setHistoryItemsLoading(false);
    }
  };

  const handleSelectRow = (row) => {
    setSelectedRequest(row);
    fetchDetail(row.wh_doc_id);
  };

  const handleApprove = (row) => {
    getAlert("C_DIALOG", `อนุมัติใบขอเบิก ${row.wh_doc_no} ?`, async (confirmed) => {
      if (!confirmed) return;
      setActionLoading(true);
      try {
        const res = await api.post(`/app/admin/withdraw-requests/${row.wh_doc_id}/approve`, {});
        if (res.data?.status === "OK") {
          setSelectedRequest(null);
          setDetail(null);
          fetchRequests();
        } else {
          getAlert("WARNING", res.data?.message || "ไม่สามารถอนุมัติได้");
        }
      } catch (err) {
        getAlert("FAILED", err?.response?.data?.message || "เกิดข้อผิดพลาด");
      } finally {
        setActionLoading(false);
      }
    });
  };

  const openRejectModal = (row) => {
    setRejectTarget(row);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectTarget) return;
    setActionLoading(true);
    try {
      const res = await api.post(`/app/admin/withdraw-requests/${rejectTarget.wh_doc_id}/reject`, { reason: rejectReason });
      if (res.data?.status === "OK") {
        getAlert("OK", "ปฏิเสธใบขอเบิกสำเร็จ");
        setShowRejectModal(false);
        setSelectedRequest(null);
        setDetail(null);
        fetchRequests();
      } else {
        getAlert("WARNING", res.data?.message || "ไม่สามารถปฏิเสธได้");
      }
    } catch (err) {
      getAlert("FAILED", err?.response?.data?.message || "เกิดข้อผิดพลาด");
    } finally {
      setActionLoading(false);
    }
  };

  const loadCreateMasterData = async () => {
    try {
      const [wh, items, groups] = await Promise.all([
        getDataWithComp("Tb_Set_WH", "AND WH_Status = 'Y' ORDER BY WH_Name ASC"),
        getDataWithComp("Tb_Set_Item", "AND Item_Status = 'Y' ORDER BY Item_Name ASC"),
        getDataWithComp("Tb_Set_ItmGroup", "AND Group_Status = 'Y' ORDER BY Group_Name ASC"),
      ]);
      setWhList(Array.isArray(wh) ? wh : []);
      setItemList(Array.isArray(items) ? items.map((it) => ({ ...it, _imgSrc: buildImageSrc(normalizeImageValue(it.Item_Img)) })) : []);
      setGroupList(Array.isArray(groups) ? groups : []);
    } catch {
      setWhList([]);
      setItemList([]);
    }
  };

  const openCreateModal = async () => {
    setIsEditMode(false);
    setEditTarget(null);
    setCreateForm({ f_wh_id: "", t_wh_id: "", remark: "" });
    setCreateItems([]);
    setShowCreateModal(true);
    await loadCreateMasterData();
  };

  const openEditModal = async (row) => {
    if (!row?.wh_doc_id) return;
    setCreateLoading(true);
    try {
      const res = await api.get(`/app/admin/withdraw-requests/${row.wh_doc_id}`);
      if (res.data?.status !== "OK" || !res.data?.result) {
        getAlert("FAILED", res.data?.message || "ไม่สามารถโหลดข้อมูลเพื่อแก้ไขได้");
        return;
      }
      const req = res.data.result;
      const mappedItems = Array.isArray(req.items)
        ? req.items.map((it) => ({
            item_id: Number(it.item_id ?? it.Item_Id),
            item_code: it.item_code ?? it.Item_Code ?? "",
            item_name: it.item_name ?? it.Item_Name ?? "",
            unit: it.unit ?? it.Item_Unit ?? "",
            qty: Number(it.qty ?? it.Item_Qty ?? 0) || 1,
          }))
        : [];
      setIsEditMode(true);
      setEditTarget(row);
      setCreateForm({
        f_wh_id: String(req.f_wh_id ?? req.F_WH_Id ?? ""),
        t_wh_id: String(req.t_wh_id ?? req.T_WH_Id ?? ""),
        remark: req.remark ?? req.WHDoc_Remark ?? "",
      });
      setCreateItems(mappedItems);
      setShowCreateModal(true);
      await loadCreateMasterData();
    } catch (err) {
      getAlert("FAILED", err?.response?.data?.message || "เกิดข้อผิดพลาด");
    } finally {
      setCreateLoading(false);
    }
  };

  const openItemPicker = async () => {
    const preChecked = {};
    createItems.forEach((r) => { preChecked[r.item_id] = r.qty; });
    setPickerChecked(preChecked);
    setPickerSearch("");
    setPickerGroup("");
    setShowItemPicker(true);
    try {
      const rows = await getDataWithComp("API_1101_WH_ITEM_ONHAND", "");
      const map = {};
      (Array.isArray(rows) ? rows : []).forEach((r) => {
        const id = Number(r.Item_Id);
        const qty = Number(r.Item_Onhand ?? r.Item_OnHand ?? r.Item_Qty ?? 0);
        if (Number.isFinite(id)) map[id] = (map[id] ?? 0) + (Number.isFinite(qty) ? qty : 0);
      });
      setOnhandMap(map);
    } catch {
      setOnhandMap({});
    }
  };

  const confirmItemPicker = () => {
    const selected = Object.entries(pickerChecked)
      .filter(([, qty]) => qty > 0)
      .map(([idStr, qty]) => {
        const id = Number(idStr);
        const item = itemList.find((it) => it.Item_Id === id);
        if (!item) return null;
        return { item_id: item.Item_Id, item_code: item.Item_Code, item_name: item.Item_Name, unit: item.Item_Unit_IN || item.Item_Unit_ST || "", qty };
      })
      .filter(Boolean);
    setCreateItems(selected);
    setShowItemPicker(false);
  };

  const addCreateItem = (itemId) => {
    if (!itemId) return;
    const item = itemList.find((it) => it.Item_Id === itemId);
    if (!item) return;
    setCreateItems((prev) => {
      const exists = prev.find((r) => r.item_id === item.Item_Id);
      if (exists) return prev;
      return [...prev, { item_id: item.Item_Id, item_code: item.Item_Code, item_name: item.Item_Name, unit: item.Item_Unit_IN || "", qty: 1 }];
    });
  };

  const updateCreateItemQty = (idx, val) => {
    setCreateItems((prev) => prev.map((r, i) => i === idx ? { ...r, qty: val } : r));
  };

  const removeCreateItem = (idx) => {
    setCreateItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleCreateOrEdit = async () => {
    if (!createForm.f_wh_id) { getAlert("FAILED", "กรุณาเลือกคลังต้นทาง"); return; }
    if (!createForm.t_wh_id) { getAlert("FAILED", "กรุณาเลือกคลังปลายทาง"); return; }
    if (createForm.f_wh_id === createForm.t_wh_id) { getAlert("FAILED", "คลังต้นทางและปลายทางต้องไม่ใช่คลังเดียวกัน"); return; }
    if (createItems.length === 0) { getAlert("FAILED", "กรุณาเพิ่มรายการสินค้า"); return; }
    const invalidQty = createItems.find((r) => !r.qty || Number(r.qty) <= 0);
    if (invalidQty) { getAlert("FAILED", `กรุณากรอกจำนวนของ "${invalidQty.item_name}"`); return; }
    setCreateLoading(true);
    try {
      const payload = {
        f_wh_id: Number(createForm.f_wh_id),
        t_wh_id: Number(createForm.t_wh_id),
        remark: createForm.remark || "",
        items: createItems.map((r, i) => ({ line: i + 1, item_id: r.item_id, item_code: r.item_code, item_name: r.item_name, qty: Number(r.qty), unit: r.unit })),
      };
      const res = isEditMode && editTarget?.wh_doc_id
        ? await api.put(`/app/admin/withdraw-requests/${editTarget.wh_doc_id}`, payload)
        : await api.post("/app/admin/withdraw-requests", payload);
      if (res.data?.status === "OK") {
        getAlert("OK", isEditMode ? "แก้ไขใบขอเบิกสำเร็จ" : "สร้างใบขอเบิกสำเร็จ");
        setShowCreateModal(false);
        setIsEditMode(false);
        setEditTarget(null);
        setSelectedRequest(null);
        setDetail(null);
        fetchRequests();
      } else {
        getAlert("FAILED", res.data?.message || (isEditMode ? "ไม่สามารถแก้ไขใบขอเบิกได้" : "ไม่สามารถสร้างใบขอเบิกได้"));
      }
    } catch (err) {
      getAlert("FAILED", err?.response?.data?.message || "เกิดข้อผิดพลาด");
    } finally {
      setCreateLoading(false);
    }
  };

  const breadcrumbs = [
    { label: "สต๊อกสินค้า", link: "/stock/stock-overview" },
    { label: "อนุมัติใบขอเบิก" },
  ];

  return (
    <div className="WarehouseStock">
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="btn btn-toggle-sidebar"
        style={{
          position: "fixed", top: "1rem",
          left: isSidebarOpen ? "190px" : "-15px",
          zIndex: 1001, background: "transparent", border: "none",
          color: "#E13F57", fontSize: "20px",
        }}
      >
        {isSidebarOpen ? "✖" : "☰"}
      </button>

      <div className="wrapper">
        <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
        <div
          className={`main-panel ${isSidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
          style={{ height: "100vh", overflow: "hidden" }}
        >
          <Topbar breadcrumbs={breadcrumbs} />

          <div className="container-fluid pl-2 pr-4 pt-3 pb-3" style={{ marginTop: "40px", height: "calc(100vh - 56px)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div className="card border-0 shadow-sm" style={{ borderRadius: 12, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div className="card-body p-3" style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Filter bar */}
            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: 12 }}>
              <div className="card-body py-3">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div className="d-flex align-items-center gap-2">
                    <i className="fas fa-filter" style={{ color: THEME }}></i>
                    <label className="mb-0" style={{ fontSize: 14, color: "#374151", fontWeight: 500 }}>สถานะ:</label>
                  </div>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 220, borderRadius: 8, fontSize: 14, height: 36 }}
                    value={filterStatus}
                    onChange={(e) => {
                      setFilterStatus(e.target.value);
                      setSelectedRequest(null);
                      setDetail(null);
                      setCurrentPage(1);
                    }}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <button
                    className="btn btn-sm"
                    style={{ backgroundColor: THEME, color: "#fff", borderRadius: 8, fontSize: 14 }}
                    onClick={fetchRequests}
                  >
                    <i className="fas fa-sync-alt me-1"></i>รีเฟรช
                  </button>
                  <button
                    className="btn btn-sm ms-auto"
                    style={{ backgroundColor: "#16a34a", color: "#fff", borderRadius: 8, fontSize: 14 }}
                    onClick={openCreateModal}
                  >
                    <i className="fas fa-plus me-1"></i>สร้างใบขอเบิก
                  </button>
                </div>
              </div>
            </div>

            {/* Main content */}
            <div className="row g-3 align-items-stretch" style={{ flex: 1, overflow: "hidden" }}>

              {/* รายการ */}
              <div className="col-lg-5" style={{ height: "100%", overflow: "hidden" }}>
                <div className="card border-0 shadow-sm" style={{ borderRadius: 12, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
                  <div className="card-header" style={{ borderRadius: "12px 12px 0 0" }}>
                    <ul className="nav nav-tabs card-header-tabs mb-0">
                      <li className="nav-item">
                        <button
                          style={{ cursor: "pointer", color: THEME }}
                          className={`nav-link ${leftTab === "list" ? "active" : ""}`}
                          onClick={() => setLeftTab("list")}
                        >
                          <i className="fas fa-clipboard-list me-1"></i>รายการใบขอเบิก
                          <span className="badge ms-1" style={{ backgroundColor: THEME, fontSize: 10 }}>{requests.length}</span>
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          style={{ cursor: "pointer", color: THEME }}
                          className={`nav-link ${leftTab === "history" ? "active" : ""}`}
                          onClick={() => { setLeftTab("history"); setHistorySelected(null); setHistoryItems([]); }}
                        >
                          <i className="fas fa-history me-1"></i>ประวัติการเบิก
                        </button>
                      </li>
                    </ul>
                  </div>

                  {/* Tab: รายการใบขอเบิก */}
                  {leftTab === "list" && (
                  <>
                  <div className="card-body p-0" style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                    {loading ? (
                      <div className="text-center py-5">
                        <Spin size="small" />
                        <p className="mt-2 text-muted" style={{ fontSize: 14 }}>กำลังโหลด...</p>
                      </div>
                    ) : requests.length === 0 ? (
                      <div className="text-center py-5">
                        <i className="fas fa-inbox" style={{ fontSize: 40, color: "#d1d5db" }}></i>
                        <p className="mt-2 text-muted" style={{ fontSize: 14 }}>ไม่มีรายการ</p>
                      </div>
                    ) : (
                      requests.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((row) => {
                        const isSelected = selectedRequest?.wh_doc_id === row.wh_doc_id;
                        return (
                          <div
                            key={row.wh_doc_id}
                            onClick={() => handleSelectRow(row)}
                            style={{
                              padding: "14px 16px",
                              borderBottom: "1px solid #f3f4f6",
                              cursor: "pointer",
                              backgroundColor: isSelected ? "#fff5f6" : "#fff",
                              borderLeft: isSelected ? `3px solid ${THEME}` : "3px solid transparent",
                              transition: "background-color 0.15s",
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-start mb-1">
                              <span style={{ fontWeight: 600, color: "#111827", fontSize: 14 }}>{row.wh_doc_no || "-"}</span>
                              <StatusBadge status={row.status} />
                            </div>
                            <div style={{ fontSize: 14, color: "#6b7280" }}>
                              <span><i className="fas fa-users me-1"></i>{row.team_name || "ไม่ระบุทีม"}</span>
                              <span className="ms-3"><i className="fas fa-box me-1"></i>{row.total_items} รายการ</span>
                            </div>
                            <div style={{ fontSize: 14, color: "#9ca3af", marginTop: 3 }}>
                              <i className="fas fa-clock me-1"></i>{formatDate(row.request_date || row.created_date)}
                              {!row.team_id && (
                                <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 600, color: "#7c3aed", backgroundColor: "#ede9fe", borderRadius: 20, padding: "2px 8px" }}>
                                  Admin ทำการเบิก
                                </span>
                              )}
                            </div>
                            {(row.f_wh_name || row.t_wh_name) && (
                              <div style={{ fontSize: 14, color: "#6b7280", marginTop: 3 }}>
                                {row.f_wh_name && <span>จาก: {row.f_wh_name}</span>}
                                {row.f_wh_name && row.t_wh_name && <span className="mx-1">→</span>}
                                {row.t_wh_name && <span>ไป: {row.t_wh_name}</span>}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                  {/* Pagination — DataTable style */}
                  {requests.length > 0 && (
                    <div className="data-table-footer">
                      <Pagination
                        totalItems={requests.length}
                        itemsPerPage={PAGE_SIZE}
                        currentPage={currentPage}
                        compact
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={(val) => {
                          setCurrentPage(1);
                          setPageSizeState(Number(val));
                        }}
                      />
                    </div>
                  )}
                  </>
                  )}

                  {/* Tab: ประวัติการเบิก */}
                  {leftTab === "history" && (
                  <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    <style>
                      {`
                        .withdraw-approval-history-ant-table .ant-table-thead > tr > th {
                          background: ${TABLE_HEADER_BG} !important;
                          color: #fff !important;
                          font-weight: 600 !important;
                        }
                      `}
                    </style>
                    <div style={{ padding: "8px 12px", borderBottom: "1px solid #f3f4f6" }}>
                      <input
                        type="text"
                        placeholder="ค้นหา..."
                        value={historySearch}
                        onChange={(e) => setHistorySearch(e.target.value)}
                        className="form-control form-control-sm"
                        style={{ fontSize: 13, borderRadius: 8 }}
                      />
                    </div>
                    <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                      <DataTable
                        className="withdraw-approval-history-ant-table"
                        columns={[
                          { title: "ลำดับ", width: 44, align: "center", render: (_, r) => r.__globalIndex },
                          {
                            title: "เลขที่เอกสาร",
                            ellipsis: true,
                            render: (_, r) => dashCentered(r.wh_doc_no || "-"),
                          },
                          {
                            title: "รอบงาน",
                            align: "center",
                            ellipsis: true,
                            render: (_, r) => dashCentered(formatJobRoundCell(r)),
                          },
                          {
                            title: "คลังรถ (IN/OUT)",
                            ellipsis: true,
                            render: (_, r) => historyWarehouseSummaryCell(r),
                          },
                          { title: "รายการ", width: 56, align: "center", render: (_, r) => r.total_items },
                          {
                            title: "วันที่",
                            ellipsis: true,
                            render: (_, r) => dashCentered(formatDate(r.request_date || r.created_date)),
                          },
                        ]}
                        dataSource={history.filter((r) => {
                          const kw = historySearch.toLowerCase();
                          return !kw ||
                            (r.wh_doc_no || "").toLowerCase().includes(kw) ||
                            formatJobRoundCell(r).toLowerCase().includes(kw) ||
                            String(r.round_no ?? "").includes(kw) ||
                            (r.team_name || "").toLowerCase().includes(kw) ||
                            (r.f_wh_name || "").toLowerCase().includes(kw) ||
                            (r.t_wh_name || "").toLowerCase().includes(kw) ||
                            (r.team_wh_name || "").toLowerCase().includes(kw) ||
                            String(r.stc_doc_id || "").includes(kw) ||
                            (r.stc_ao_doc_no || "").toLowerCase().includes(kw);
                        })}
                        loading={historyLoading}
                        rowKey={(r) =>
                          r._source === "job_close_stc"
                            ? `stc:${r.stc_doc_id}:${r.stc_wh_id}:${r.wh_doc_no || ""}`
                            : String(r.wh_doc_id)
                        }
                        defaultPageSize={100}
                        onRow={(r) => ({ onClick: () => fetchHistoryItems(r), style: { cursor: "pointer" } })}
                      />
                    </div>
                  </div>
                  )}

                </div>
              </div>

              {/* รายละเอียด */}
              <div className="col-lg-7" style={{ height: "100%", overflow: "hidden" }}>
                <div className="card border-0 shadow-sm" style={{ borderRadius: 12, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
                  <div
                    className="card-header py-3"
                    style={{
                      backgroundColor: "#fff",
                      borderTop: "1px solid #e5e7eb",
                      borderLeft: "1px solid #e5e7eb",
                      borderRight: "1px solid #e5e7eb",
                      borderBottom: "2px solid #e5e7eb",
                      borderRadius: "12px 12px 0 0",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-2">
                        <i className="fas fa-file-alt" style={{ color: THEME, fontSize: 16 }}></i>
                        <h6 className="mb-0" style={{ fontWeight: 600, color: "#111827" }}>
                          {selectedRequest ? `รายละเอียด: ${selectedRequest.wh_doc_no}` : "รายละเอียด"}
                        </h6>
                      </div>
                      {selectedRequest?.status === 1 && (
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm"
                            style={{ backgroundColor: "#f59e0b", color: "#fff", borderRadius: 8, fontSize: 14 }}
                            onClick={() => openEditModal(selectedRequest)}
                            disabled={actionLoading || createLoading}
                          >
                            <i className="fas fa-pen me-1"></i>แก้ไข
                          </button>
                          <button
                            className="btn btn-sm"
                            style={{ backgroundColor: "#16a34a", color: "#fff", borderRadius: 8, fontSize: 14 }}
                            onClick={() => handleApprove(selectedRequest)}
                            disabled={actionLoading}
                          >
                            <i className="fas fa-check me-1"></i>อนุมัติ
                          </button>
                          <button
                            className="btn btn-sm"
                            style={{ backgroundColor: "#dc2626", color: "#fff", borderRadius: 8, fontSize: 14 }}
                            onClick={() => openRejectModal(selectedRequest)}
                            disabled={actionLoading}
                          >
                            <i className="fas fa-times me-1"></i>ปฏิเสธ
                          </button>
                        </div>
                      )}
                      {selectedRequest?.status === 4 && (
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm"
                            style={{ backgroundColor: "#dc2626", color: "#fff", borderRadius: 8, fontSize: 14 }}
                            onClick={() => openRejectModal(selectedRequest)}
                            disabled={actionLoading}
                          >
                            <i className="fas fa-times me-1"></i>ยกเลิกการอนุมัติ
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="card-body" style={{ flex: 1, minHeight: 0, maxHeight: "calc(100vh - 300px)", overflowY: "auto" }}>
                    {leftTab === "history" ? (
                      /* ===== HISTORY DETAIL ===== */
                      !historySelected ? (
                        <div className="text-center py-5">
                          <i className="fas fa-mouse-pointer" style={{ fontSize: 40, color: "#d1d5db" }}></i>
                          <p className="mt-2 text-muted" style={{ fontSize: 14 }}>เลือกรายการทางซ้ายเพื่อดูรายละเอียด</p>
                        </div>
                      ) : historyItemsLoading ? (
                        <div className="text-center py-5"><Spin size="small" /></div>
                      ) : (
                        <>
                          <div className="row g-3 mb-3">
                            {[
                              { label: "เลขที่เอกสาร", value: historySelected.wh_doc_no || "-" },
                              { label: "รอบงาน", value: formatJobRoundCell(historySelected) },
                              ...(historySelected._jobCloseOut
                                ? [{ label: "อ้างอิงปฏิทิน (Calendar_Id)", value: historySelected.stc_doc_id }]
                                : []),
                              {
                                label: "คลังรถ (มุมมอง IN/OUT)",
                                value: historyWarehouseSummaryCell(historySelected),
                              },
                              { label: "วันที่", value: formatDate(historySelected.request_date || historySelected.created_date) },
                            ].map(({ label, value }) => (
                              <div className="col-6" key={label}>
                                <div style={{ fontSize: 13, color: "#9ca3af" }}>{label}</div>
                                <div style={{ fontSize: 14, color: "#111827", fontWeight: 500 }}>{dashCentered(value)}</div>
                              </div>
                            ))}
                          </div>
                          <h6 style={{ fontWeight: 600, color: "#374151", fontSize: 14, marginBottom: 8 }}>
                            <i className="fas fa-boxes me-2" style={{ color: TABLE_HEADER_BG }}></i>
                            รายการสินค้า ({historyItems.length} รายการ)
                          </h6>
                          <div className="table-responsive">
                            <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                              <thead>
                                <tr style={{ backgroundColor: TABLE_HEADER_BG }}>
                                  <th style={{ color: "#fff", fontWeight: 600, border: "none", width: 36 }}>#</th>
                                  <th style={{ color: "#fff", fontWeight: 600, border: "none" }}>ชื่อสินค้า</th>
                                  <th style={{ color: "#fff", fontWeight: 600, border: "none", textAlign: "center", width: 100 }}>IN/OUT คลังรถ</th>
                                  <th style={{ color: "#fff", fontWeight: 600, border: "none", textAlign: "right", width: 80 }}>จำนวน</th>
                                  <th style={{ color: "#fff", fontWeight: 600, border: "none", width: 70 }}>หน่วย</th>
                                </tr>
                              </thead>
                              <tbody>
                                {historyItems.length === 0 ? (
                                  <tr><td colSpan={5} className="text-center text-muted py-3">ไม่มีข้อมูล</td></tr>
                                ) : historyItems.map((item, idx) => {
                                  const vLabel = historySelected?._jobCloseOut
                                    ? "OUT"
                                    : getVehicleWarehouseInOutLabel(item, historySelected);
                                  const docType = item.Doc_Type ?? item.doc_type ?? "";
                                  const qty = Number(item.STC_QTY ?? item.Item_Qty ?? item.stc_qty ?? 0);
                                  return (
                                    <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                                      <td style={{ color: "#9ca3af" }}>{item.Line ?? idx + 1}</td>
                                      <td style={{ color: "#111827" }}>
                                        {dashCentered(item.Item_Name ?? item.item_name ?? "-")}
                                      </td>
                                      <td className="text-center">
                                        {vLabel === "IN"
                                          ? <span style={{ color: "#16a34a", fontWeight: 600, fontSize: 12 }}>IN</span>
                                          : vLabel === "OUT"
                                            ? <span style={{ color: "#dc2626", fontWeight: 600, fontSize: 12 }}>OUT</span>
                                            : (
                                              <span style={{
                                                color: "#6b7280",
                                                display: (docType || "-") === "-" ? "block" : undefined,
                                                width: (docType || "-") === "-" ? "100%" : undefined,
                                                textAlign: (docType || "-") === "-" ? "center" : undefined,
                                              }}
                                              >
                                                {docType || "-"}
                                              </span>
                                            )}
                                      </td>
                                      <td style={{ textAlign: "right", fontWeight: 600 }}>{qty.toLocaleString()}</td>
                                      <td style={{ color: "#6b7280" }}>
                                        {dashCentered(item.Item_Unit ?? item.item_unit ?? "-")}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )
                    ) : (
                    /* ===== NORMAL DETAIL ===== */
                    !selectedRequest ? (
                      <div className="text-center py-5">
                        <i className="fas fa-mouse-pointer" style={{ fontSize: 40, color: "#d1d5db" }}></i>
                        <p className="mt-2 text-muted" style={{ fontSize: 14 }}>เลือกรายการทางซ้ายเพื่อดูรายละเอียด</p>
                      </div>
                    ) : detailLoading ? (
                      <div className="text-center py-5">
                        <Spin size="small" />
                        <p className="mt-2 text-muted" style={{ fontSize: 14 }}>กำลังโหลด...</p>
                      </div>
                    ) : detail ? (
                      <>
                        <div className="row g-3 mb-4">
                          {[
                            { label: "เลขที่เอกสาร", value: detail.wh_doc_no },
                            { label: "สถานะ", value: <StatusBadge status={detail.status} /> },
                            { label: "รอบงาน", value: formatJobRoundCell(detail) },
                            { label: "วันที่ช่างขอเบิก", value: formatDate(detail.request_date) },
                            { label: "คลังต้นทาง", value: detail.f_wh_name || "-" },
                            { label: "คลังปลายทาง", value: detail.t_wh_name || "-" },
                            { label: "สร้างโดย", value: detail.created_by || "-" },
                            { label: "หมายเหตุ", value: detail.remark || "-" },
                          ].map(({ label, value }) => (
                            <div className="col-6" key={label}>
                              <div style={{ fontSize: 14, color: "#9ca3af" }}>{label}</div>
                              <div style={{ fontSize: 14, color: "#111827", fontWeight: 500 }}>{dashCentered(value)}</div>
                            </div>
                          ))}
                        </div>

                        <h6 style={{ fontWeight: 600, color: "#374151", fontSize: 14, marginBottom: 12 }}>
                          <i className="fas fa-boxes me-2" style={{ color: THEME }}></i>
                          รายการสินค้า ({(detail.items || []).length} รายการ)
                        </h6>
                        <div className="table-responsive">
                          <table className="table table-sm table-hover" style={{ fontSize: 14 }}>
                            <thead>
                              <tr style={{ backgroundColor: "#E13F57" }}>
                                <th style={{ color: "#fff", fontWeight: 600, border: "none", width: 40 }}>#</th>
                                <th style={{ color: "#fff", fontWeight: 600, border: "none" }}>รหัสสินค้า</th>
                                <th style={{ color: "#fff", fontWeight: 600, border: "none" }}>ชื่อสินค้า</th>
                                <th style={{ color: "#fff", fontWeight: 600, border: "none", textAlign: "right" }}>จำนวน</th>
                                <th style={{ color: "#fff", fontWeight: 600, border: "none" }}>หน่วย</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(detail.items || []).map((item, idx) => (
                                <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                                  <td style={{ color: "#9ca3af" }}>{item.line ?? idx + 1}</td>
                                  <td style={{ color: "#6b7280" }}>{dashCentered(item.item_code || "-")}</td>
                                  <td style={{ color: "#111827" }}>{dashCentered(item.item_name || "-")}</td>
                                  <td style={{
                                    textAlign: (item.qty ?? "-") === "-" ? "center" : "right",
                                    color: "#111827",
                                    fontWeight: 600,
                                  }}
                                  >
                                    {dashCentered(item.qty ?? "-")}
                                  </td>
                                  <td style={{ color: "#6b7280" }}>{dashCentered(item.unit || "-")}</td>
                                </tr>
                              ))}
                              {(detail.items || []).length === 0 && (
                                <tr>
                                  <td colSpan={5} className="text-center text-muted py-3">ไม่มีรายการสินค้า</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : null
                    )}
                  </div>
                </div>
              </div>

            </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div
          className="modal d-flex"
          style={{ position: "fixed", inset: 0, zIndex: 1050, backgroundColor: "rgba(0,0,0,0.45)", alignItems: "center", justifyContent: "center" }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowCreateModal(false); }}
        >
          <div className="card border-0 shadow-lg" style={{ borderRadius: 14, width: "100%", maxWidth: 680, margin: 16, maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
            <div className="card-header py-3" style={{ borderBottom: "1px solid #e5e7eb", borderRadius: "14px 14px 0 0", flexShrink: 0 }}>
              <div className="d-flex align-items-center gap-2">
                <i className="fas fa-file-medical" style={{ color: "#16a34a" }}></i>
                <h6 className="mb-0" style={{ fontWeight: 600, color: "#111827" }}>
                  {isEditMode ? `แก้ไขใบขอเบิก${editTarget?.wh_doc_no ? `: ${editTarget.wh_doc_no}` : ""}` : "สร้างใบขอเบิก"}
                </h6>
              </div>
            </div>
            <div className="card-body p-4" style={{ overflowY: "auto", flex: 1 }}>
              {/* คลังต้นทาง + ปลายทาง + หมายเหตุ */}
              <div className="row g-3 mb-3">
                <div className="col-4">
                  <label className="form-label" style={{ fontSize: 13, fontWeight: 500 }}>คลังต้นทาง <span style={{ color: "red" }}>*</span></label>
                  <select
                    className="form-select form-select-sm"
                    value={createForm.f_wh_id}
                    onChange={(e) => setCreateForm((f) => ({ ...f, f_wh_id: e.target.value }))}
                  >
                    <option value="">-- เลือกคลัง --</option>
                    {whList.map((w) => <option key={w.WH_Id} value={w.WH_Id}>{w.WH_Name}</option>)}
                  </select>
                </div>
                <div className="col-4">
                  <label className="form-label" style={{ fontSize: 13, fontWeight: 500 }}>คลังปลายทาง <span style={{ color: "red" }}>*</span></label>
                  <select
                    className="form-select form-select-sm"
                    value={createForm.t_wh_id}
                    onChange={(e) => setCreateForm((f) => ({ ...f, t_wh_id: e.target.value }))}
                  >
                    <option value="">-- เลือกคลัง --</option>
                    {whList.filter((w) => String(w.WH_Id) !== String(createForm.f_wh_id)).map((w) => <option key={w.WH_Id} value={w.WH_Id}>{w.WH_Name}</option>)}
                  </select>
                </div>
                <div className="col-4">
                  <label className="form-label" style={{ fontSize: 13, fontWeight: 500 }}>หมายเหตุ</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="หมายเหตุ (ไม่บังคับ)"
                    value={createForm.remark}
                    onChange={(e) => setCreateForm((f) => ({ ...f, remark: e.target.value }))}
                  />
                </div>
              </div>

              {/* เพิ่มสินค้า */}
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="form-label mb-0" style={{ fontSize: 13, fontWeight: 500 }}>รายการสินค้า</label>
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: "#E13F57", color: "#fff", borderRadius: 8, fontSize: 13 }}
                  onClick={openItemPicker}
                >
                  <i className="fas fa-plus me-1"></i>เลือกสินค้า
                </button>
              </div>

              {/* ตารางสินค้า */}
              {createItems.length > 0 && (
                <table className="table table-sm" style={{ fontSize: 13 }}>
                  <thead>
                    <tr style={{ backgroundColor: "#E13F57" }}>
                      <th style={{ color: "#fff", fontWeight: 600, border: "none" }}>#</th>
                      <th style={{ color: "#fff", fontWeight: 600, border: "none" }}>รหัส</th>
                      <th style={{ color: "#fff", fontWeight: 600, border: "none" }}>ชื่อสินค้า</th>
                      <th style={{ color: "#fff", fontWeight: 600, border: "none", width: 90 }}>จำนวน</th>
                      <th style={{ color: "#fff", fontWeight: 600, border: "none", width: 60 }}>หน่วย</th>
                      <th style={{ border: "none", width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {createItems.map((r, idx) => (
                      <tr key={r.item_id}>
                        <td style={{ color: "#9ca3af" }}>{idx + 1}</td>
                        <td style={{ color: "#6b7280" }}>{r.item_code}</td>
                        <td>{r.item_name}</td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm text-center"
                            data-create-qty="true"
                            value={r.qty}
                            min={1}
                            onChange={(e) => updateCreateItemQty(idx, e.target.value)}
                            style={{ width: 80 }}
                            onFocus={(e) => e.target.select()}
                            onKeyDown={(e) => {
                              if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                                e.preventDefault();
                                const all = Array.from(document.querySelectorAll("[data-create-qty]"));
                                const i = all.indexOf(e.currentTarget);
                                const next = e.key === "ArrowDown" ? all[i + 1] : all[i - 1];
                                if (next) { next.focus(); next.select(); }
                              }
                            }}
                          />
                        </td>
                        <td style={{ color: "#6b7280" }}>{r.unit}</td>
                        <td>
                          <button className="btn btn-sm btn-danger py-0 px-2" onClick={() => removeCreateItem(idx)}>
                            <i className="fas fa-times"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {createItems.length === 0 && (
                <div className="text-center py-3" style={{ color: "#9ca3af", fontSize: 13 }}>ยังไม่มีรายการสินค้า</div>
              )}
            </div>
            <div className="card-footer d-flex justify-content-end gap-2 py-3" style={{ borderTop: "1px solid #e5e7eb", borderRadius: "0 0 14px 14px", backgroundColor: "#f9fafb", flexShrink: 0, alignItems: "stretch" }}>
              <button
                className="btn px-4"
                style={{ borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13, backgroundColor: "#fff" }}
                onClick={() => setShowCreateModal(false)}
                disabled={createLoading}
              >
                ยกเลิก
              </button>
              <button
                className="btn px-4"
                style={{ backgroundColor: "#16a34a", color: "#fff", borderRadius: 8, fontSize: 13 }}
                onClick={handleCreateOrEdit}
                disabled={createLoading}
              >
                {createLoading
                  ? <Spin size="small" className="me-1" />
                  : <i className="fas fa-save me-1"></i>
                }
                {isEditMode ? "บันทึกการแก้ไข" : "บันทึก"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Item Picker Modal */}
      {showItemPicker && (() => {
        const groupMap = {};
        groupList.forEach((g) => { groupMap[g.Group_Id] = g.Group_Name; });
        const filtered = itemList.filter((it) => {
          const kw = pickerSearch.toLowerCase();
          const matchSearch = !kw || (it.Item_Name || "").toLowerCase().includes(kw) || (it.Item_Code || "").toLowerCase().includes(kw);
          const matchGroup = !pickerGroup || String(it.Item_Group) === String(pickerGroup);
          return matchSearch && matchGroup;
        });
        const checkedCount = Object.values(pickerChecked).filter((q) => q > 0).length;
        return (
          <div
            className="modal d-flex"
            style={{ position: "fixed", inset: 0, zIndex: 1060, backgroundColor: "rgba(0,0,0,0.45)", alignItems: "center", justifyContent: "center" }}
            onClick={(e) => { if (e.target === e.currentTarget) setShowItemPicker(false); }}
          >
            <div className="card border-0 shadow-lg" style={{ borderRadius: 14, width: "fit-content", maxWidth: "95vw", minWidth: 600, margin: 16, maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
              <div className="card-header py-3" style={{ borderBottom: "1px solid #e5e7eb", borderRadius: "14px 14px 0 0", flexShrink: 0 }}>
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <i className="fas fa-boxes" style={{ color: "#E13F57" }}></i>
                    <h6 className="mb-0" style={{ fontWeight: 600, color: "#111827" }}>เลือกสินค้า</h6>
                  </div>
                  <div className="d-flex gap-2 align-items-center">
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 180, fontSize: 13, height: 32 }}
                      value={pickerGroup}
                      onChange={(e) => setPickerGroup(e.target.value)}
                    >
                      <option value="">หมวดหมู่: ทั้งหมด</option>
                      {groupList.map((g) => <option key={g.Group_Id} value={String(g.Group_Id)}>{g.Group_Name}</option>)}
                    </select>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="ค้นหารหัส / ชื่อสินค้า..."
                      style={{ width: 220, fontSize: 13, height: 32 }}
                      value={pickerSearch}
                      onChange={(e) => setPickerSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                </div>
              </div>
              <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
                <table className="table table-sm table-hover mb-0" style={{ fontSize: 13, tableLayout: "auto", width: "100%" }}>
                  <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                    <tr style={{ backgroundColor: "#E13F57" }}>
                      <th style={{ color: "#fff", fontWeight: 600, border: "none", width: 50, textAlign: "center" }}>รูป</th>
                      <th style={{ color: "#fff", fontWeight: 600, border: "none", width: 40, textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={filtered.length > 0 && filtered.every((it) => pickerChecked[it.Item_Id] > 0)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setPickerChecked((prev) => {
                                const next = { ...prev };
                                filtered.forEach((it) => { if (!next[it.Item_Id]) next[it.Item_Id] = 1; });
                                return next;
                              });
                            } else {
                              setPickerChecked((prev) => {
                                const next = { ...prev };
                                filtered.forEach((it) => { delete next[it.Item_Id]; });
                                return next;
                              });
                            }
                          }}
                        />
                      </th>
                      <th style={{ color: "#fff", fontWeight: 600, border: "none" }}>หมวดหมู่</th>
                      <th style={{ color: "#fff", fontWeight: 600, border: "none" }}>รหัสสินค้า</th>
                      <th style={{ color: "#fff", fontWeight: 600, border: "none" }}>ชื่อสินค้า</th>
                      <th style={{ color: "#fff", fontWeight: 600, border: "none", width: 80, textAlign: "center" }}>หน่วย</th>
                      <th style={{ color: "#fff", fontWeight: 600, border: "none", width: 90, textAlign: "center" }}>คงเหลือ</th>
                      <th style={{ color: "#fff", fontWeight: 600, border: "none", width: 90, textAlign: "center" }}>จำนวน</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr><td colSpan={8} className="text-center text-muted py-4">ไม่พบสินค้า</td></tr>
                    ) : filtered.map((it, idx) => {
                      const isChecked = !!pickerChecked[it.Item_Id];
                      return (
                        <tr
                          key={it.Item_Id}
                          style={{ backgroundColor: isChecked ? "#fffbeb" : idx % 2 === 0 ? "#fff" : "#f9fafb", cursor: "pointer", whiteSpace: "nowrap" }}
                          onClick={() => {
                            setPickerChecked((prev) => {
                              const next = { ...prev };
                              if (next[it.Item_Id]) { delete next[it.Item_Id]; } else { next[it.Item_Id] = 1; }
                              return next;
                            });
                          }}
                        >
                          <td className="text-center" onClick={(e) => e.stopPropagation()} style={{ padding: "6px 8px" }}>
                            {it._imgSrc
                              ? <img src={it._imgSrc} alt="" style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 6, border: "1px solid #e5e7eb" }} />
                              : <div style={{ width: 80, height: 80, borderRadius: 6, background: "#f3f4f6", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><i className="fas fa-image" style={{ color: "#d1d5db", fontSize: 28 }}></i></div>
                            }
                          </td>
                          <td className="text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                setPickerChecked((prev) => {
                                  const next = { ...prev };
                                  if (e.target.checked) { next[it.Item_Id] = 1; } else { delete next[it.Item_Id]; }
                                  return next;
                                });
                              }}
                            />
                          </td>
                          <td style={{ color: "#6b7280" }}>{dashCentered(groupMap[it.Item_Group] || "-")}</td>
                          <td style={{ color: "#6b7280" }}>{dashCentered(it.Item_Code || "-")}</td>
                          <td style={{ color: "#111827", fontWeight: isChecked ? 500 : 400 }}>
                            {dashCentered(it.Item_Name || "-")}
                          </td>
                          <td className="text-center" style={{ color: "#6b7280" }}>
                            {dashCentered(it.Item_Unit_IN || it.Item_Unit_ST || "-")}
                          </td>
                          <td className="text-center" style={{ fontWeight: 500, color: (onhandMap[it.Item_Id] ?? 0) <= 0 ? "#dc2626" : "#16a34a" }}>
                            {(onhandMap[it.Item_Id] ?? 0).toLocaleString()}
                          </td>
                          <td className="text-center" onClick={(e) => e.stopPropagation()}>
                            {isChecked ? (
                              <input
                                type="number"
                                className="form-control form-control-sm text-center"
                                data-picker-qty="true"
                                value={pickerChecked[it.Item_Id]}
                                min={1}
                                style={{ width: 70 }}
                                onChange={(e) => setPickerChecked((prev) => ({ ...prev, [it.Item_Id]: Number(e.target.value) || 1 }))}
                                onClick={(e) => e.stopPropagation()}
                                onFocus={(e) => e.target.select()}
                                onKeyDown={(e) => {
                                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                                    e.preventDefault();
                                    const all = Array.from(document.querySelectorAll("[data-picker-qty]"));
                                    const idx = all.indexOf(e.currentTarget);
                                    const next = e.key === "ArrowDown" ? all[idx + 1] : all[idx - 1];
                                    if (next) { next.focus(); next.select(); }
                                  }
                                }}
                              />
                            ) : <span style={{ color: "#d1d5db" }}>-</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="card-footer d-flex justify-content-between align-items-center py-3" style={{ borderTop: "1px solid #e5e7eb", borderRadius: "0 0 14px 14px", backgroundColor: "#f9fafb", flexShrink: 0 }}>
                <span style={{ fontSize: 13, color: "#374151" }}>เลือกแล้ว <b style={{ color: "#E13F57" }}>{checkedCount}</b> รายการ</span>
                <div className="d-flex gap-2" style={{ alignItems: "stretch" }}>
                  <button className="btn" style={{ borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13, backgroundColor: "#fff", flex: 1 }} onClick={() => setShowItemPicker(false)}>ยกเลิก</button>
                  <button className="btn" style={{ backgroundColor: "#E13F57", color: "#fff", borderRadius: 8, fontSize: 13, flex: 1, whiteSpace: "nowrap" }} onClick={confirmItemPicker}>
                    เพิ่มรายการ
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Reject Modal */}
      {showRejectModal && (
        <div
          className="modal d-flex"
          style={{
            position: "fixed", inset: 0, zIndex: 1050,
            backgroundColor: "rgba(0,0,0,0.4)",
            alignItems: "center", justifyContent: "center",
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowRejectModal(false); }}
        >
          <div className="card border-0 shadow-lg" style={{ borderRadius: 14, width: "100%", maxWidth: 460, margin: 16 }}>
            <div className="card-header py-3" style={{ borderBottom: "1px solid #e5e7eb", borderRadius: "14px 14px 0 0" }}>
              <div className="d-flex align-items-center gap-2">
                <i className="fas fa-exclamation-triangle" style={{ color: "#dc2626" }}></i>
                <h6 className="mb-0" style={{ fontWeight: 600, color: "#111827" }}>
                  ปฏิเสธใบขอเบิก: {rejectTarget?.wh_doc_no}
                </h6>
              </div>
            </div>
            <div className="card-body p-4">
              <label className="form-label" style={{ fontSize: 14, color: "#374151", fontWeight: 500 }}>
                เหตุผล (ไม่บังคับ)
              </label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="ระบุเหตุผลการปฏิเสธ..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                style={{ borderRadius: 8, fontSize: 14 }}
              />
            </div>
            <div
              className="card-footer d-flex justify-content-end gap-2 py-3"
              style={{ borderTop: "1px solid #e5e7eb", borderRadius: "0 0 14px 14px", backgroundColor: "#f9fafb" }}
            >
              <button
                className="btn btn-sm px-4"
                style={{ borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13, backgroundColor: "#fff" }}
                onClick={() => setShowRejectModal(false)}
                disabled={actionLoading}
              >
                ยกเลิก
              </button>
              <button
                className="btn btn-sm px-4"
                style={{ backgroundColor: "#dc2626", color: "#fff", borderRadius: 8, fontSize: 13 }}
                onClick={handleRejectConfirm}
                disabled={actionLoading}
              >
                {actionLoading
                  ? <Spin size="small" className="me-1" />
                  : <i className="fas fa-times me-1"></i>
                }
                ยืนยันปฏิเสธ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
